import { Checkbox, TableCell, TableRow, Tooltip } from "@mui/material";
import { entityOfItem, LensHit } from "Backend";
import { FC, useContext, useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCaseInfo } from "redux/slices/case";
import { PreviousFormCardState, UseFormCard, useFormCard } from "../useFormCard";
import theme, { EllipsisSX } from "theme";
import SyncFormGroupValues from "../HitCard/SyncFormGroupValues";
import { HitTableContext } from "contexts/HitTableContext";
import { Cell, DocumentIntake } from "generated/graphql";
import HitTableEditRow from "./HitTableEditRow";
import LensHitActions from "./LensHitActions";
import displayCellValue from "components/FormField/displayCellValue";
import { validateCell } from "helpers/FormHelpers";

interface SelectHitProps {
  hit: LensHit;
  checked: boolean;
  setChecked: (hit: LensHit, checked: boolean) => void;
}

const DOCUMENT_COLUMN_WIDTH = 65;

const SelectHit: FC<SelectHitProps> = ({ hit, checked, setChecked }) => {
  return hit.blockInForm ? (
    <Tooltip title="Existing responses can be edited in profile" arrow>
      <span>
        <Checkbox disabled checked />
      </span>
    </Tooltip>
  ) : (
    <Checkbox checked={checked} onChange={(_evt, checked) => setChecked(hit, checked)} />
  );
};

interface WrapperProps {
  previousState: PreviousFormCardState | undefined;
  intake: DocumentIntake | undefined;
  hit: LensHit;
  formCard: UseFormCard;
  children: React.ReactNode;
  fieldId: number;
}
const Wrapper: FC<WrapperProps> = ({ children, previousState, intake, hit, formCard, fieldId }) => {
  return previousState === undefined ? (
    <SyncFormGroupValues
      fieldId={fieldId}
      block={hit.block}
      useFormCard={formCard}
      clusterType={hit.clusterType}
      pageType={hit.pageType}
      entity={entityOfItem(hit)}
      lensRuleDescription={hit.ruleDescription}
      lensRuleSubtype={hit.ruleSubtype}
      docPageCount={intake?.pages || 1}>
      {children}
    </SyncFormGroupValues>
  ) : (
    <>{children}</>
  );
};

interface HitTableRowProps {
  hit: LensHit;
  isActive: boolean;
  onViewFinding: () => void;
  checked: boolean;
  setChecked: (hit: LensHit, checked: boolean) => void;
  inBulkEdit: boolean;
  defaultDrugTableColumns: Cell[];
  onHitIgnored: (hit: LensHit) => void;
}

const HitTableRow: FC<HitTableRowProps> = ({
  hit,
  onViewFinding,
  isActive,
  checked,
  setChecked,
  inBulkEdit,
  defaultDrugTableColumns,
  onHitIgnored,
}) => {
  const { lensId, caseId, formField, intake, hitInEdit, setHitInEdit, formCardMap, setFormCardMap } =
    useContext(HitTableContext);
  const caseInfo = useAppSelector(selectCaseInfo);
  const previousFormCard = formCardMap.get(hit.hitId);
  const previousState: PreviousFormCardState | undefined =
    previousFormCard !== undefined
      ? {
          formGroupValuesState: previousFormCard.formGroupValuesState[0] ?? [],
          pageRangeState: previousFormCard.pageRangeState[0],
          fieldAnsweredState: previousFormCard.fieldAnsweredState[0],
          notes: previousFormCard.notesHandle[0],
        }
      : undefined;
  const formCard = useFormCard({
    lensId,
    caseId,
    intakeId: hit.block.documentIntakeId,
    intakePage: hit.block.originalPageNumber,
    blockIndex: hit.block.index,
    initialFieldId: formField.id,
    previousState,
  });
  const inSingleEdit = hitInEdit === hit.hitId;
  const selectHit = <SelectHit hit={hit} checked={checked} setChecked={setChecked} />;
  const hitActions = (
    <LensHitActions hit={hit} onHitIgnored={onHitIgnored} ignoreCollapsedHits lensId={lensId} caseId={caseId} />
  );

  const onClick = () => {
    setHitInEdit(hit.blockInForm || hitInEdit === hit.hitId ? undefined : hit.hitId);
    onViewFinding();
  };

  useEffect(() => {
    if (checked) {
      setFormCardMap(new Map(formCardMap.set(hit.hitId, formCard)));
    } else {
      formCardMap.delete(hit.hitId);
      setFormCardMap(new Map(formCardMap));
    }
  }, [formCard.canSave, checked, formCard.formGroupValuesState[0]]);

  return (
    <Wrapper previousState={previousState} intake={intake} hit={hit} fieldId={formField.id} formCard={formCard}>
      <TableRow
        sx={{
          cursor: "pointer",
          ":hover": {
            backgroundColor: isActive ? "white" : "grey.50",
          },
        }}>
        {formField.cells.length > formCard.formGroupValuesState[0].length ? (
          <TableCell colSpan={formField.cells.length + 1}>Loading...</TableCell>
        ) : (
          <>
            {(!inSingleEdit || inBulkEdit) && (
              <>
                <TableCell>{selectHit}</TableCell>
                <TableCell
                  onClick={onClick}
                  sx={{ ...EllipsisSX, maxWidth: DOCUMENT_COLUMN_WIDTH }}
                  title={hit.block.documentName}>
                  {hit.block.documentName}
                </TableCell>

                <TableCell onClick={onClick}>{hit.block.originalPageNumber}</TableCell>
              </>
            )}
            {inBulkEdit && !inSingleEdit ? (
              formField.cells.map((cell) => {
                const trueCellIndex = formField.cells.findIndex((c) => c.name === cell.name);
                const cellIsValid = validateCell(formCard.formGroupValuesState[0][trueCellIndex], cell);

                return (
                  <TableCell
                    onClick={onClick}
                    key={cell.id}
                    sx={{
                      borderBottom: !cellIsValid ? `1px solid ${theme.palette.error.main}` : undefined,
                    }}>
                    {displayCellValue(formCard.formGroupValuesState[0][trueCellIndex]?.value, caseInfo)}
                  </TableCell>
                );
              })
            ) : inBulkEdit && inSingleEdit ? (
              <HitTableEditRow
                selectHit={selectHit}
                caseInfo={caseInfo}
                cells={formField.cells}
                stateHandle={formCard.formGroupValuesState}
                hitActions={hitActions}
              />
            ) : inSingleEdit ? (
              <HitTableEditRow
                selectHit={selectHit}
                caseInfo={caseInfo}
                cells={formField.cells}
                stateHandle={formCard.formGroupValuesState}
                colSpan={defaultDrugTableColumns.length + 2 || 1}
                hitActions={hitActions}
              />
            ) : (
              defaultDrugTableColumns.map((cell) => {
                const trueCellIndex = formField.cells.findIndex((c) => c.name === cell.name);
                return (
                  <TableCell onClick={onClick} key={cell.id}>
                    {displayCellValue(formCard.formGroupValuesState[0][trueCellIndex]?.value, caseInfo)}
                  </TableCell>
                );
              })
            )}
          </>
        )}
      </TableRow>
    </Wrapper>
  );
};

export default HitTableRow;
